import React, { useState, useMemo } from 'react';
import { X, User, DollarSign, Calendar, FileText } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { userService, advanceService } from '../services/api';
import toast from 'react-hot-toast';

const ManualAdvanceModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    user_id: '',
    amount: '',
    ref_month: new Date().getMonth() + 1,
    ref_year: new Date().getFullYear(),
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: usersData } = useQuery({
    queryKey: ['users-for-select'],
    queryFn: () => userService.getUsers({ limit: 100, active: true }),
    enabled: isOpen,
  });

  const users = useMemo(() => {
    if (!usersData) return [];
    if (Array.isArray(usersData?.data?.users)) return usersData.data.users;
    if (Array.isArray(usersData?.data?.data?.users)) return usersData.data.data.users;
    if (Array.isArray(usersData?.data)) return usersData.data;
    return [];
  }, [usersData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.user_id) {
      toast.error('Selecione um colaborador');
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) < 1) {
      toast.error('Informe um valor válido');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        user_id: parseInt(formData.user_id, 10),
        amount: parseFloat(formData.amount),
        ref_month: parseInt(formData.ref_month, 10),
        ref_year: parseInt(formData.ref_year, 10),
        notes: formData.notes?.trim() || null,
      };

      const response = await advanceService.createManualAdvance(payload);

      if (response.data?.success || response.status === 200 || response.status === 201) {
        toast.success('Adiantamento criado com sucesso!');
        onSuccess();
        onClose();
        setFormData({
          user_id: '',
          amount: '',
          ref_month: new Date().getMonth() + 1,
          ref_year: new Date().getFullYear(),
          notes: '',
        });
      } else {
        toast.error(response.data?.message || 'Erro ao criar adiantamento');
      }
    } catch (error) {
      console.error('Erro ao criar adiantamento manual:', error);
      toast.error(error.response?.data?.message || 'Erro ao criar adiantamento');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const months = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' },
  ];

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Lançar Adiantamento Manual</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <User className="w-4 h-4 inline mr-1" />
              Colaborador *
            </label>
            <select
              name="user_id"
              value={formData.user_id}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Selecione um colaborador...</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.full_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <DollarSign className="w-4 h-4 inline mr-1" />
              Valor *
            </label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              min="1"
              max="5500"
              step="0.01"
              placeholder="Ex: 500.00"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Limite: R$ 1,00 a R$ 5.500,00</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Mês *
              </label>
              <select
                name="ref_month"
                value={formData.ref_month}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ano *
              </label>
              <select
                name="ref_year"
                value={formData.ref_year}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                {years.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <FileText className="w-4 h-4 inline mr-1" />
              Observações
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              placeholder="Motivo do adiantamento manual..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Criando...' : 'Lançar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualAdvanceModal;
